import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { Box, Stack } from "@similarweb/ui-components/foundations";

import { showErrorToast } from "app/actions/toast_actions";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { QueryStates } from "app/pages/sales-intelligence/common-components/QueryStates/QueryStates";
import { TEAMS_MANAGEMENT_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import usePreventLeavePage from "app/pages/sales-intelligence/hooks/usePreventLeavePage";
import { DeleteTeamModal } from "app/pages/sales-intelligence/modules/teams/components/DeleteTeamModal/DeleteTeamModal";
import { TeamModalContainer } from "app/pages/sales-intelligence/modules/teams/components/TeamModal/TeamModalContainer";
import { useDeleteTeamMutation } from "app/pages/sales-intelligence/modules/teams/queries/useDeleteTeamMutation";
import { useTeamsListQuery } from "app/pages/sales-intelligence/modules/teams/queries/useTeamsListQuery";
import type { TeamDetailTabHandle } from "app/pages/sales-intelligence/pages/management/types/teamDetail.types";
import type { TeamDetailTab } from "app/pages/sales-intelligence/services/navigation/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { StyledManagementSIBreadcrumbs } from "../SavedSearchWebhooksManagementContainer/styles";

import { StyledTeamDetailContainer } from "./styles";
import { TeamDetailHeader } from "./TeamDetailHeader";
import { TeamDetailTabsCard } from "./TeamDetailTabsCard";
import { UnsavedChangesModal } from "./UnsavedChangesModal";

type ModalMode = "settings" | "add-members" | null;

const TAB = {
    members: 0,
    filters: 1,
    scoring: 2,
} as const;

// Single mapping between the `tab` route param and the local tab index, so a deep link
// (e.g. from a teams table cell) can open the Filters or Lead scoring tab directly.
const TAB_INDEX_BY_NAME: Record<TeamDetailTab, number> = {
    members: TAB.members,
    filters: TAB.filters,
    scoring: TAB.scoring,
};

type PendingTabSwitch = {
    from: number;
    to: number;
};

export const TeamDetailContainer = () => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();
    const { params } = useCurrentStateAndParams();
    const parsedTeamId = Number(params.id);
    const hasValidTeamId = Number.isFinite(parsedTeamId);
    const teamId = parsedTeamId;

    const initialTabIndex = TAB_INDEX_BY_NAME[params.tab as TeamDetailTab] ?? TAB.members;
    const isNewTeam = Boolean(params.isNewTeam);

    useEffect(() => {
        if (!hasValidTeamId) {
            void proNavigator.go(TEAMS_MANAGEMENT_ROUTE);
        }
    }, [hasValidTeamId]);

    const [modalMode, setModalMode] = useState<ModalMode>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState<number>(initialTabIndex);
    const [pendingTabSwitch, setPendingTabSwitch] = useState<PendingTabSwitch | null>(null);
    const [isPageLeaveModalOpen, setIsPageLeaveModalOpen] = useState(false);
    // Once the Filters or Lead scoring tab is visited, keep its panel mounted so subsequent tab
    // switches don't re-fire the bootstrap thunks (signals/suppression refetch on
    // every dispatch, which would flash a partial loading state every time).
    const [hasVisitedFilters, setHasVisitedFilters] = useState(initialTabIndex === TAB.filters);
    const [hasVisitedScoring, setHasVisitedScoring] = useState(initialTabIndex === TAB.scoring);
    const filtersRef = useRef<TeamDetailTabHandle>(null);
    const scoringRef = useRef<TeamDetailTabHandle>(null);

    const editableTabRefs = useMemo<Record<number, RefObject<TeamDetailTabHandle | null>>>(
        () => ({ [TAB.filters]: filtersRef, [TAB.scoring]: scoringRef }),
        [],
    );

    const getHasUnsavedChanges = useCallback(
        (): boolean => editableTabRefs[tabIndex]?.current?.hasUnsavedChanges() ?? false,
        [editableTabRefs, tabIndex],
    );

    const { setConfirm: confirmLeavePage, resetRedirectToState } = usePreventLeavePage(
        getHasUnsavedChanges,
        () => {
            setPendingTabSwitch(null);
            setIsPageLeaveModalOpen(true);
        },
        [],
        { name: TEAMS_MANAGEMENT_ROUTE, params: {} },
    );

    const {
        data: teams,
        isLoading: isTeamsLoading,
        isFetching: isTeamsFetching,
        isError: isTeamsError,
        refetch: refetchTeams,
    } = useTeamsListQuery({ enabled: hasValidTeamId });

    const { mutateAsync: deleteTeam, isPending: isDeleting } = useDeleteTeamMutation();

    const team = teams?.find((t) => t.teamId === teamId);
    const isTeamMissing = !team;
    // A team missing from a still-fetching list means "not loaded yet" — e.g. right
    // after creation, before the list refetch resolves — not a genuine error.
    const showLoadingState = isTeamsLoading || (isTeamMissing && isTeamsFetching && !isTeamsError);
    const showErrorState = isTeamsError || (isTeamMissing && !isTeamsLoading && !isTeamsFetching);

    const handleRetryQueries = useCallback(() => {
        void refetchTeams();
    }, [refetchTeams]);

    const breadcrumbs = useMemo(
        () => [
            { state: TEAMS_MANAGEMENT_ROUTE, title: translate("si.management.teams.header") },
            { state: "", title: team?.name ?? translate("si.management.teams.detail.title") },
        ],
        [team?.name, translate],
    );

    const handleBreadcrumbClick = useCallback(({ state }: { state: string }) => {
        if (state) {
            void proNavigator.go(state);
        }
    }, []);

    const handleOpenSettings = useCallback(() => setModalMode("settings"), []);
    const handleOpenAddMembers = useCallback(() => setModalMode("add-members"), []);
    const handleCloseModal = useCallback(() => setModalMode(null), []);

    const handleOpenDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);

    const handleCancelDelete = useCallback(() => {
        if (isDeleting) {
            return;
        }
        setIsDeleteModalOpen(false);
    }, [isDeleting]);

    const handleConfirmDelete = useCallback(async () => {
        try {
            await deleteTeam(teamId);
            setIsDeleteModalOpen(false);
            resetRedirectToState();
            confirmLeavePage(true);
            void proNavigator.go(TEAMS_MANAGEMENT_ROUTE);
        } catch {
            dispatch(
                showErrorToast(
                    renderErrorToast({
                        content: translate("si.common.toast.error"),
                        minWidth: "auto",
                    }),
                ),
            );
            // Keep the confirmation modal open so the user can retry.
        }
    }, [confirmLeavePage, deleteTeam, dispatch, resetRedirectToState, teamId, translate]);

    const markTabVisited = useCallback((target: number) => {
        if (target === TAB.filters) {
            setHasVisitedFilters(true);
        }
        if (target === TAB.scoring) {
            setHasVisitedScoring(true);
        }
    }, []);

    const handleSelectTab = useCallback(
        (target: number): void => {
            if (target === tabIndex) {
                return;
            }
            if (editableTabRefs[tabIndex]?.current?.hasUnsavedChanges()) {
                setPendingTabSwitch({ from: tabIndex, to: target });
                return;
            }
            markTabVisited(target);
            setTabIndex(target);
        },
        [tabIndex, editableTabRefs, markTabVisited],
    );

    const handleConfirmSwitch = useCallback(() => {
        if (isPageLeaveModalOpen) {
            editableTabRefs[tabIndex]?.current?.discardChanges();
            setIsPageLeaveModalOpen(false);
            confirmLeavePage(true);
            return;
        }

        if (pendingTabSwitch) {
            editableTabRefs[pendingTabSwitch.from]?.current?.discardChanges();
            markTabVisited(pendingTabSwitch.to);
            setTabIndex(pendingTabSwitch.to);
            setPendingTabSwitch(null);
        }
    }, [confirmLeavePage, editableTabRefs, isPageLeaveModalOpen, markTabVisited, pendingTabSwitch, tabIndex]);

    const handleCancelSwitch = useCallback(() => {
        setPendingTabSwitch(null);
        setIsPageLeaveModalOpen(false);
        resetRedirectToState();
    }, [resetRedirectToState]);

    const showQueryStates = showLoadingState || showErrorState || !team;

    return (
        <Stack
            flexGrow={1}
            minHeight="0"
            data-automation="team-detail-page"
        >
            <StyledManagementSIBreadcrumbs
                list={breadcrumbs}
                onClick={handleBreadcrumbClick}
                dataAutomation="team-detail-breadcrumbs"
            />
            <Box height="34px" />
            <StyledTeamDetailContainer>
                {showQueryStates ? (
                    <QueryStates
                        isLoading={showLoadingState}
                        isError={showErrorState}
                        onRetry={handleRetryQueries}
                        loadingDataAutomation="team-detail-loading"
                        errorDataAutomation="team-detail-query-error"
                        errorRetryDataAutomation="team-detail-query-error-retry"
                    />
                ) : (
                    <>
                        <TeamDetailHeader
                            name={team.name}
                            description={team.description}
                            onEdit={handleOpenSettings}
                            onDelete={handleOpenDeleteModal}
                        />
                        <TeamDetailTabsCard
                            tabIndex={tabIndex}
                            onSelectTab={handleSelectTab}
                            teamId={teamId}
                            team={team}
                            isNewTeam={isNewTeam}
                            hasVisitedFilters={hasVisitedFilters}
                            hasVisitedScoring={hasVisitedScoring}
                            filtersRef={filtersRef}
                            scoringRef={scoringRef}
                            onAddMembers={handleOpenAddMembers}
                        />
                    </>
                )}
            </StyledTeamDetailContainer>

            {modalMode !== null && team ? (
                <TeamModalContainer
                    isOpen
                    team={team}
                    onClose={handleCloseModal}
                    initialStep={modalMode === "add-members" ? "members" : "name"}
                    lockedToStep
                />
            ) : null}

            {team ? (
                <DeleteTeamModal
                    isOpen={isDeleteModalOpen}
                    teamName={team.name ?? ""}
                    isDeleting={isDeleting}
                    onCancel={handleCancelDelete}
                    onConfirm={() => void handleConfirmDelete()}
                />
            ) : null}

            <UnsavedChangesModal
                isOpen={pendingTabSwitch !== null || isPageLeaveModalOpen}
                onCancel={handleCancelSwitch}
                onConfirm={handleConfirmSwitch}
            />
        </Stack>
    );
};
